import React, {useEffect, useState} from "react";
import {Typography} from "@mui/material";
import {useNavigate} from "react-router";
import {HowToRegOutlined} from "@mui/icons-material";

import {ageInStage} from "./ApprovalsSection";
import {signableSlots} from "../../util/approvalSlots";
import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import {Blue3, Grey5, Grey7, Grey17, White} from "../../theme/styles";

// What is waiting on this person's SIGNATURE, on the page they land on.
//
// THE DASHBOARD PROMISED THIS AND DID NOT DELIVER IT. Its subtitle reads "What needs you, and where
// to pick up", and the only thing it showed was training. Reported by Ed: Elena Marchetti sits on
// the Document Control Board, SOP-00239 rev 2 had been waiting on that board with no signatures, and
// her home page said nothing at all. Nothing was broken -- the queue had it, the sidebar had a link
// to the queue -- but a signature nobody is told about is a document that stops moving, and the
// person blocking it does not know they are.
//
// A SIGNATURE OUTRANKS TRAINING, so this sits above the training panel. Training is an obligation on
// YOU; an unsigned approval is holding up somebody else's record as well.
//
// IT NEVER SIGNS. Signing needs the record in front of you and a password, and that belongs on the
// queue where the diagram, the drawer and the rejection path all live. Every route out of here goes
// to /approvals.
//
// QUIET ON FAILURE, like the training panel: a landing page showing an error box because one panel
// could not load is worse than one missing a panel, and the queue itself says what went wrong.

const SHOWN = 5;

type Row = Record<string, any>;

export default function ApprovalAttention() {
    const sessionContext = useSessionContext();
    const navigate = useNavigate();
    const [waiting, setWaiting] = useState<Row[] | null>(null);

    // MEMOISED, or the effect below re-runs on every render and fetches forever.
    const api = React.useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);

    useEffect(() => {
        let cancelled = false;
        // THE SAME "mine" THE QUEUE OPENS ON, so the two can never disagree about what needs this
        // person -- one endpoint, one definition of awaiting my signature.
        api({method: "GET", url: "t1/v1/object_approval_requests", params: {View: "mine"}})
            .then(response => { if (!cancelled) setWaiting(response.data?.Requests ?? []); })
            .catch(() => { if (!cancelled) setWaiting([]); })
        return () => { cancelled = true; };
    }, [api]);

    if (waiting === null || waiting.length === 0) return null;   // nothing waiting: draw nothing

    const now = Date.now();
    const shown = waiting.slice(0, SHOWN);

    return (
        <div style={{marginTop: 28}}>
            <div style={{display: "flex", alignItems: "center", gap: 16, padding: "18px 22px",
                         backgroundColor: White, border: "1px solid #EAECF0", borderRadius: 12,
                         boxSizing: "border-box"}}>
                <div style={{width: 40, height: 40, borderRadius: 10, backgroundColor: "#EFF8FF",
                             display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <HowToRegOutlined sx={{color: Blue3, fontSize: 22}}/>
                </div>
                <div>
                    <Typography style={{fontSize: 28, fontWeight: 700, color: Grey17, lineHeight: 1.1}}>
                        {waiting.length}
                    </Typography>
                    {/* NAMES THE ACT, not the object. "2 approvals" could be things you submitted. */}
                    <Typography style={{fontSize: 13, color: Grey7}}>
                        waiting on your signature
                    </Typography>
                </div>
            </div>

            <div style={{border: "1px solid #EAECF0", borderRadius: 12, marginTop: 12,
                         overflow: "hidden", backgroundColor: White}}>
                {shown.map((request, index) => {
                    // WHICH SLOT IS ACTUALLY THEIRS. A request can name several personas in a stage
                    // and this person may hold only one of them; the queue resolves that already.
                    const slots = signableSlots(request);
                    const meaning = slots[0]?.Meaning;
                    const age = ageInStage(request, now);
                    return (
                        <div key={request.USID ?? index}
                             onClick={() => navigate("/approvals")}
                             style={{display: "flex", alignItems: "center", gap: 12,
                                     padding: "11px 16px", cursor: "pointer",
                                     borderTop: index ? "1px solid #EAECF0" : undefined}}>
                            <Typography style={{fontSize: 13.5, fontWeight: 600, color: Blue3,
                                                whiteSpace: "nowrap", flexShrink: 0}}>
                                {request.RecordObjectID ?? "—"}
                            </Typography>
                            <Typography title={request.RecordName ?? ""}
                                        style={{fontSize: 13.5, color: Grey17, flex: 1, minWidth: 0,
                                                whiteSpace: "nowrap", overflow: "hidden",
                                                textOverflow: "ellipsis"}}>
                                {request.RecordName}
                            </Typography>
                            {meaning ? (
                                <Typography style={{fontSize: 12, color: Grey7, flexShrink: 0,
                                                    whiteSpace: "nowrap"}}>
                                    to {String(meaning).toLowerCase()}
                                </Typography>
                            ) : null}
                            {age ? (
                                <Typography style={{fontSize: 12, color: Grey5, flexShrink: 0,
                                                    whiteSpace: "nowrap"}}>
                                    {age}
                                </Typography>
                            ) : null}
                        </div>
                    );
                })}
            </div>

            {waiting.length > shown.length ? (
                <Typography onClick={() => navigate("/approvals")}
                            style={{fontSize: 13, color: Blue3, cursor: "pointer", marginTop: 10}}>
                    View all {waiting.length} →
                </Typography>
            ) : null}
        </div>
    );
}
